import { RestrictedRoute } from "RestrictedRoute";
import { QuickNavModal } from "components/QuickNavModal/QuickNavModal";
import { SETTINGS_APP_QUICK_NAV_PAGES } from "constants/quickNav/settings";
import { AdminNotificationDashboard } from "components/AdminNotification/AdminNotificationDashboard";
import { AppLayout } from "components/AppLayout/AppLayout";
import classNames from "classnames";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import { RoutesAccrossApps } from "components/RoutesAcrossApps/RoutesAcrossApps";
import { LeftBar, LeftBarOpenContext } from "components/Sidebar/LeftBar";
import { SettingsNav } from "components/Settings/SettingsNav";
import { SearchNavItem } from "components/Sidebar/SearchNavItem";
import { useHasCommandPalette } from "hooks/useHasCommandPalette";
import { Tickets as CustomerTickets } from "components/Ticket/CustomerTicket/Tickets";
import { TopBar } from "components/TopBar/TopBar";
import BillingTabBar from "components/billing/BillingTabBar";
import { ConditionalLink } from "components/conditionalLink";
import { PageNotFound } from "components/design/PageNotFound";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { ApiSettings } from "components/ApiSettings/ApiSettings";
import { ClaudeIntegration } from "components/ClaudeIntegration/ClaudeIntegration";
import AddTeamMember from "components/teamMembers/addTeamMember";
import { Settings } from "components/userProfile/settings";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useQuery } from "hooks/useQuery";
import { useContext, useEffect } from "react";
import { Redirect, Switch, useRouteMatch } from "react-router-dom";
import { DashboardBanner } from "signInSignUp/DashboardBanner";
import "static/styles/containers/dashboard.css";
import { AuditLogs } from "./AuditLogs/AuditLogs";
import { LockedAuditLogs } from "./AuditLogs/LockAuditLogs";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { Services } from "./Services/Services";

const Logo = ({ redirectTo }: { redirectTo: string }) => {
  const { open } = useContext(LeftBarOpenContext);

  return (
    <ConditionalLink
      to={redirectTo}
      className={classNames(
        "t-my-0.5 t-flex t-h-9 t-items-center t-gap-2 t-rounded-md t-text-text-30 t-no-underline hover:t-bg-neutral-0",
        {
          "t-px-3": open,
          "t-justify-center t-px-0": !open,
        }
      )}
    >
      <span className="t-flex t-h-5 t-w-5 t-shrink-0 t-items-center t-justify-center">
        <ArrowLeft size="16" />
      </span>
      {open && <span className="t-text-body-sm">Settings</span>}
    </ConditionalLink>
  );
};

export const SettingsDashboard = () => {
  const { path } = useRouteMatch();
  const query = useQuery();
  const settingBackTo = query.get("settings_back_to") || "";
  const { isCustomer, isAdmin } = useRoleBasedView();
  const { hasCustomerPalette } = useHasCommandPalette();

  const { setItem, localData: back_link } = useLocalStorage(
    "settings_back_to",
    ""
  );

  const redirectTo = back_link || "/tax";
  const stickyNav = hasCustomerPalette && <SearchNavItem />;

  useEffect(() => {
    if (settingBackTo) {
      setItem(settingBackTo);
    }
  }, [settingBackTo]);

  return (
    <AppLayout
      sidebar={
        <LeftBar
          showCollapse
          logo={<Logo redirectTo={redirectTo} />}
          stickyNav={stickyNav}
        >
          <SettingsNav />
        </LeftBar>
      }
      topbar={
        <TopBar
          border
          logo={<Logo redirectTo={redirectTo} />}
          shouldCenterLogo={false}
          nav={<SettingsNav />}
          stickyNav={stickyNav}
        />
      }
      banner={<DashboardBanner />}
    >
      <QuickNavModal pages={SETTINGS_APP_QUICK_NAV_PAGES} />
      <Switch>
        <RestrictedRoute path={`${path}/services`}>
          <Services />
        </RestrictedRoute>
        <RestrictedRoute path={`${path}/auditlog`}>
          <PermissionBasedView
            product_type="TAX"
            route="/settings/auditlog"
            blockedUI={<LockedAuditLogs />}
          >
            <AuditLogs />
          </PermissionBasedView>
        </RestrictedRoute>
        <RestrictedRoute path={`${path}/billing`}>
          <BillingTabBar />
        </RestrictedRoute>
        <RestrictedRoute path={`${path}/tickets`} component={CustomerTickets} />

        <RestrictedRoute path={`${path}/team`}>
          <AddTeamMember />
        </RestrictedRoute>

        <RestrictedRoute exact path={`${path}`}>
          <Redirect to={`${path}/profile`} />
        </RestrictedRoute>

        <RestrictedRoute
          exact
          path={`${path}/books-settings`}
          render={(props) => {
            return (
              <Redirect to={`/books/books-settings/${props.location.search}`} />
            );
          }}
        />

        <RestrictedRoute exact path={`${path}/profile`} component={Settings} />
        <RestrictedRoute
          exact
          path={`${path}/claude-integration`}
          component={ClaudeIntegration}
        />
        <RestrictedRoute
          exact
          path={`${path}/api-settings`}
          component={isCustomer ? ApiSettings : PageNotFound}
        />
        <RestrictedRoute
          path={`${path}/notifications`}
          component={isAdmin ? AdminNotificationDashboard : PageNotFound}
        />
        <RoutesAccrossApps />
      </Switch>
    </AppLayout>
  );
};
